/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _concatInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/concat";
import _toConsumableArray from "@babel/runtime-corejs3/helpers/esm/toConsumableArray";
import _sliceInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/slice";
import _URL from "@babel/runtime-corejs3/core-js-stable/url";
import _Number$isNaN from "@babel/runtime-corejs3/core-js-stable/number/is-nan";
import _parseInt from "@babel/runtime-corejs3/core-js-stable/parse-int";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _Promise from "@babel/runtime-corejs3/core-js-stable/promise";
import _objectSpread from "@babel/runtime-corejs3/helpers/esm/objectSpread2";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime-corejs3/helpers/esm/assertThisInitialized";
import _getPrototypeOf from "@babel/runtime-corejs3/helpers/esm/getPrototypeOf";
import _get from "@babel/runtime-corejs3/helpers/esm/get";
import _inherits from "@babel/runtime-corejs3/helpers/esm/inherits";
import _createSuper from "@babel/runtime-corejs3/helpers/esm/createSuper";

/** @module browser/classes/payload */
import { log } from '@codistica/core';
import { isSafeHeader } from '../modules/is-safe-header.js';
import { AJAXRequest } from './ajax-request.js';
/**
 * @typedef payloadPayloadDataType
 * @property {number|null} [total=null] - Payload size in bytes.
 * @property {boolean} [createObjectUrl=false] - If true, object url will be created from response. Only applies if response is of blob type.
 * @property {boolean} [force=false] - Indicates that payload usage must be forced.
 * @property {string} path - Request URL.
 * @property {string|null} [id] - Request ID.
 * @property {string} [requestMethod='GET'] - HTTP method to be used.
 * @property {(''|'arraybuffer'|'blob'|'document'|'json'|'text')} [responseType='text'] - Response type.
 * @property {boolean} [noCache=false] - If true, appends unique queries to the request URL so fresh responses are forced.
 */

/**
 * @classdesc AJAXRequest wrapper for @codistica/browser Loader class.
 */

var Payload = /*#__PURE__*/function (_AJAXRequest) {
  _inherits(Payload, _AJAXRequest);

  var _super = _createSuper(Payload);

  /**
   * @description Constructor.
   * @param {payloadPayloadDataType} payloadData - Payload data.
   */
  function Payload(payloadData) {
    var _this2 = this,
        _context,
        _context2,
        _context3,
        _context4,
        _context5,
        _context6,
        _context7,
        _context8,
        _context9;

    var _this;

    _classCallCheck(this, Payload);

    _this = _super.call(this, {
      path: payloadData.path,
      id: payloadData.id,
      requestMethod: payloadData.requestMethod,
      responseType: payloadData.responseType,
      noCache: payloadData.noCache
    });
    _this.objectUrl = null;
    _this.errors = [];
    _this.options = _objectSpread({}, _this.options, {
      createObjectUrl: typeof payloadData.createObjectUrl === 'boolean' ? payloadData.createObjectUrl : false,
      force: typeof payloadData.force === 'boolean' ? payloadData.force : false
    });
    _this.status = {
      isFromCache: null,
      isComputable: typeof payloadData.total === 'number' ? true : null
    };
    _this.stats = _objectSpread({}, _this.stats, {
      requested: 0
    });
    _this.progress = {
      total: typeof payloadData.total === 'number' ? payloadData.total : null,
      loaded: 0,
      percent: 0
    };
    _this.promise = new _Promise(_bindInstanceProperty(_context = function _context(resolve, reject) {
      _newArrowCheck(this, _this2);

      // TODO: CATCH PROMISE ERRORS
      _this.resolve = _bindInstanceProperty(resolve).call(resolve, null, _assertThisInitialized(_this));
      _this.reject = _bindInstanceProperty(reject).call(reject, null, _assertThisInitialized(_this));
    }).call(_context, this)); // BIND METHODS

    _this.send = _bindInstanceProperty(_context2 = _this.send).call(_context2, _assertThisInitialized(_this));
    _this.revertProgress = _bindInstanceProperty(_context3 = _this.revertProgress).call(_context3, _assertThisInitialized(_this));
    _this.emit = _bindInstanceProperty(_context4 = _this.emit).call(_context4, _assertThisInitialized(_this));
    _this.onEndHandler = _bindInstanceProperty(_context5 = _this.onEndHandler).call(_context5, _assertThisInitialized(_this));
    _this.onProgressHandler = _bindInstanceProperty(_context6 = _this.onProgressHandler).call(_context6, _assertThisInitialized(_this));
    _this.onHeadersHandler = _bindInstanceProperty(_context7 = _this.onHeadersHandler).call(_context7, _assertThisInitialized(_this));
    _this.onSuccessHandler = _bindInstanceProperty(_context8 = _this.onSuccessHandler).call(_context8, _assertThisInitialized(_this));
    _this.onFailHandler = _bindInstanceProperty(_context9 = _this.onFailHandler).call(_context9, _assertThisInitialized(_this)); // ATTACH HANDLERS

    _this.once('end', _this.onEndHandler);

    _this.on('progress', _this.onProgressHandler);

    _this.once('headers', _this.onHeadersHandler);

    _this.once('success', _this.onSuccessHandler);

    _this.once('fail', _this.onFailHandler);

    return _this;
  }

  _createClass(Payload, [{
    key: "send",
    value: function send() {
      this.stats.requested++;

      _get(_getPrototypeOf(Payload.prototype), "send", this).call(this);
    }
  }, {
    key: "revertProgress",
    value: function revertProgress() {
      if (this.progress.total !== null && this.progress.total !== 0) {
        this.emit('deltatotal', {
          deltaTotal: -this.progress.total
        });
      }

      if (this.progress.loaded !== 0) {
        this.emit('deltaloaded', {
          deltaLoaded: -this.progress.loaded
        });
      }

      this.status.isComputable = false;
      this.progress.total = null;
      this.progress.loaded = -1;
      this.progress.percent = -1;
    }
  }, {
    key: "onEndHandler",
    value: function onEndHandler() {
      // DETACH HANDLERS
      this.off('progress', this.onProgressHandler);
    }
    /**
     * @description Callback for progress event.
     * @param {ProgressEvent} e - Progress event.
     */

  }, {
    key: "onProgressHandler",
    value: function onProgressHandler(e) {
      if (this.status.isComputable) {
        if (e.loaded > this.progress.total) {
          log.error('Payload()', "".concat(this.path, " - LOADED EXCEEDED TOTAL. SWITCHING TO NON COMPUTABLE"))(); // REVERT PROGRESS

          this.revertProgress();
        } else {
          this.emit('deltaloaded', {
            deltaLoaded: e.loaded - this.progress.loaded
          });
          this.progress.loaded = e.loaded;
          this.progress.percent = 100 * this.progress.loaded / this.progress.total;
          this.emit('computableprogress', e);
        }
      }
    }
  }, {
    key: "onHeadersHandler",
    value: function onHeadersHandler() {
      var responseTimestamp = null;
      var total = null; // GET SOURCE

      if (isSafeHeader(this.request, ['Now'])) {
        // TODO: ANOTHER WAY TO DETECT IF FROM CACHE?
        responseTimestamp = _parseInt(this.request.getResponseHeader('Now'));

        if (!_Number$isNaN(responseTimestamp)) {
          if (responseTimestamp < this.stats.sendTimestamp) {
            // CASE: RESPONSE FROM CACHE
            this.status.isFromCache = true;
            log.verbose('Payload()', "".concat(this.path, " - RESPONSE FROM CACHE"))();
          } else {
            // CASE: RESPONSE FROM SERVER
            this.status.isFromCache = false;
            log.verbose('Payload()', "".concat(this.path, " - RESPONSE FROM SERVER"))();
          }
        }
      }

      if (this.status.isFromCache === null) {
        // CASE: UNKNOWN SOURCE
        log.verbose('Payload()', "".concat(this.path, " - UNKNOWN RESPONSE SOURCE"))();
      } // GET TOTAL


      if (isSafeHeader(this.request, ['Content-Length'])) {
        total = _parseInt(this.request.getResponseHeader('Content-Length'));

        if (!_Number$isNaN(total)) {
          log.debug('Payload()', "".concat(this.requestUrl, " - TOTAL RECEIVED FROM HEADER"))();

          if (this.progress.total !== total) {
            this.emit('deltatotal', {
              deltaTotal: total - this.progress.total
            });

            if (this.progress.total !== null) {
              log.error('Payload()', "".concat(this.path, " - SAVED TOTAL DIFFERS FROM HEADER. OVERWRITING"))();
            }
          }

          this.status.isComputable = true;
          this.progress.total = total;
        }
      }

      if (this.progress.total === null) {
        log.warning('Payload()', "".concat(this.requestUrl, " - IS NON COMPUTABLE"))();
        this.status.isComputable = false;
      }
    }
  }, {
    key: "onSuccessHandler",
    value: function onSuccessHandler() {
      if (this.options.createObjectUrl && this.options.responseType === 'blob' && this.request.response !== null) {
        this.objectUrl = _URL.createObjectURL(this.request.response);
        log.debug('Payload()', "".concat(this.requestUrl, " - CREATED OBJECT URL"))();
      } // RESOLVE PROMISE


      this.resolve();
    }
  }, {
    key: "onFailHandler",
    value: function onFailHandler() {
      // REVERT PROGRESS
      this.revertProgress(); // REJECT PROMISE

      this.reject();
    }
    /**
     * @description Event emission auxiliary method.
     * @param {(string|symbol)} event - Event name to be emitted.
     * @param {...*} args - Arguments to be passed to handlers.
     * @returns {boolean} Event emission result.
     */

  }, {
    key: "emit",
    value: function emit(event) {
      var _get2, _context10;

      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      var extraProps = args[0],
          otherArgs = _sliceInstanceProperty(args).call(args, 1);

      return (_get2 = _get(_getPrototypeOf(Payload.prototype), "emit", this)).call.apply(_get2, _concatInstanceProperty(_context10 = [this, event, _objectSpread({}, extraProps || {}, {
        that: this
      })]).call(_context10, _toConsumableArray(otherArgs || [])));
    }
  }]);

  return Payload;
}(AJAXRequest);

export { Payload };